import { bool, func, number, object, string } from "prop-types";
import React, { useMemo } from "react";

import PackageDetailDescription from "@components/Customize/shared/PackageDetailDescription";
import { Button } from "@components/shared/Button";
import FinancePrice from "@components/shared/FinancePrice";
import ScrollableContainer from "@components/shared/ScrollableContainer";
import Typography from "@components/shared/Typography";

import { getStaticAssetURL } from "@lib/getStaticAssetURL";
import { useTranslation } from "@lib/i18n";

import { CUSTOMIZE_TYPES } from "@shared/constants";

const UpsellItemDetail = props => {
  const {
    isReadOnly,
    isSelected,
    isSelectable,
    item,
    label,
    onClick,
    price,
    type,
  } = props;

  const { t } = useTranslation();

  const isAccessory = type === CUSTOMIZE_TYPES.ACCESSORY;
  const isPack = type === CUSTOMIZE_TYPES.PACK;
  const isService = type === CUSTOMIZE_TYPES.SERVICE;

  const backgroundImage = useMemo(() => {
    if (isPack) {
      return `url("${getStaticAssetURL(
        `/images/gallery/pack/${item.pack.officialReference}_big.jpg`
      )}")`;
    }

    if (isAccessory) {
      return `url("${getStaticAssetURL(
        `/images/gallery/accessory/${item.officialReference}_big.jpg`
      )}")`;
    }

    if (isService) {
      return `url("${getStaticAssetURL(`/images/gallery/service/1_big.jpg`)}")`;
    }

    return null;
  }, [item, type]);

  return (
    <div className="flex flex-col gap-4">
      <Typography className="text-primaryDark uppercase text-xl font-semibold relative z-1 pr-8">
        {label}
      </Typography>
      <div
        className="
          flex flex-col justify-between items-start w-full h-39 p-4 relative
          bg-no-repeat bg-right bg-cover rounded-md overflow-hidden shadow-sm shadow-primaryOrange/10
        "
        style={{ backgroundImage }}
      />

      <div className="flex flex-col justify-between items-center gap-4 p-4 relative text-black smallMobile:flex-row">
        {!isReadOnly && isSelectable && (
          <Button
            onClick={onClick}
            variant={isSelected ? "secondary" : "primary"}
          >
            {isSelected ? t("customize.remove") : t("customize.add")}
          </Button>
        )}
        <Typography className="text-4xl uppercase font-semibold sm:ml-auto">
          <FinancePrice amount={price} />
        </Typography>
      </div>

      <div className="flex flex-col mb-4">
        <PackageDetailDescription
          description={(item?.pack ?? item)?.description}
        />
      </div>

      {isService && (
        <div className="flex flex-col mb-4">
          <Typography
            className="uppercase font-semibold my-2"
            data-id="customize-advantages-heading"
          >
            {t("customize.advantages")}
          </Typography>
          <ul className="p-4 list-disc">
            {item.descriptionAdvantages.split(";").map((advantage, index) => (
              <li className="ml-2 text-primaryOrange" key={index}>
                <Typography className="text-sm/normal">{advantage}</Typography>
              </li>
            ))}
          </ul>
        </div>
      )}

      {(isPack || isAccessory) && (
        <ScrollableContainer
          className={{
            outer: "max-largeMobile:mx-0 max-sm:max-w-[calc(100dvw-2.5rem)]",
          }}
        >
          {(item?.pack ?? item)?.personalizationOptions.map(({ id, label }) => (
            <figure
              className="max-w-30"
              data-id={`customize-image-${label}`}
              key={id}
            >
              <img
                alt={label}
                className="size-30 object-cover rounded-sm"
                src={getStaticAssetURL(
                  `/images/gallery/personalization/${id}.jpg`
                )}
                title={label}
              />
              <figcaption className="text-black text-sm mt-3 whitespace-normal">
                {label}
              </figcaption>
            </figure>
          ))}
        </ScrollableContainer>
      )}
    </div>
  );
};

UpsellItemDetail.propTypes = {
  isReadOnly: bool,
  isSelectable: bool,
  isSelected: bool,
  item: object,
  label: string,
  onClick: func,
  price: number,
  type: string,
};

export default UpsellItemDetail;
